/**
 * Maps ISO 3166-1 alpha-2 country codes to the priority order of supported languages
 * based on how many people speak each language in that country (>=5% share) or if
 * the language is official. Arrays are ordered most-spoken first.
 * 
 * Only includes languages that are supported in the application.
 */
export const COUNTRY_LANGUAGE_ORDER_MAP: Record<string, string[]> = {
  // North America
  US: ['en', 'es'],
  CA: ['en', 'fr'],
  MX: ['es'],

  // Europe - Western
  GB: ['en'],
  FR: ['fr', 'en'],
  DE: ['de', 'en', 'fr'],
  IT: ['it', 'en', 'es', 'fr'],
  ES: ['es', 'en'],
  NL: ['nl', 'en', 'de', 'fr'],
  BE: ['nl', 'fr', 'de'],
  CH: ['de', 'fr', 'it', 'en'],
  AT: ['de', 'en'],
  IE: ['en'],
  PT: ['pt', 'en', 'fr', 'es'],
  LU: ['fr', 'de', 'en'],
  LI: ['de', 'en'],

  // Europe - Nordic
  SE: ['sv', 'en'],
  NO: ['nb', 'en'],
  DK: ['da', 'en'],
  FI: ['en', 'sv'],
  IS: ['en'],

  // Europe - Eastern
  PL: ['pl', 'en', 'de'],
  CZ: ['cs', 'en', 'de'],
  HU: ['hu', 'en', 'de'],
  RO: ['ro', 'en', 'fr'],
  HR: ['hr', 'en', 'de'],
  SK: ['en', 'de'],
  BG: ['en'],
  SI: ['en', 'de', 'it'],
  EE: ['en'],
  LV: ['en'],
  LT: ['en'],
  RS: ['en'],
  BA: ['hr', 'en', 'de'],
  ME: ['en'],
  MK: ['en'],
  AL: ['en'],
  XK: ['en'],

  // Asia - East
  CN: ['zh-CN'],
  JP: ['ja', 'en'],
  KR: ['ko', 'en'],
  TW: ['zh-CN', 'en'],
  HK: ['zh-CN', 'en'],
  MO: ['zh-CN', 'pt', 'en'],

  // Asia - Southeast
  IN: ['hi', 'en'],
  PH: ['en'],
  ID: ['en'],
  MY: ['en', 'zh-CN'],
  TH: ['en'],
  VN: ['en'],
  SG: ['en', 'zh-CN'],
  MM: ['en'],
  KH: ['en'],
  LA: ['en'],
  BN: ['en'],

  // Asia - South/Central
  PK: ['en'],
  BD: ['en'],
  NP: ['en'],
  LK: ['en'],
  AF: ['en'],
  KZ: ['en'],
  KG: ['en'],
  TJ: ['en'],
  TM: ['en'],
  UZ: ['en'],
  MN: ['en'],

  // Middle East
  TR: ['tr', 'en'],
  SA: ['en'],
  AE: ['en'],
  IR: ['en'],
  IL: ['en'],
  IQ: ['en'],
  JO: ['en'],
  LB: ['en'],
  SY: ['en'],
  YE: ['en'],
  OM: ['en'],
  KW: ['en'],
  BH: ['en'],
  QA: ['en'],
  PS: ['en'],

  // Africa - North
  EG: ['en'],
  LY: ['en'],
  TN: ['fr', 'en'],
  DZ: ['fr', 'en'],
  MA: ['fr', 'en'],
  SD: ['en'],
  SS: ['en'],

  // Africa - West
  NG: ['en'],
  GH: ['en'],
  CI: ['fr'],
  SN: ['fr'],
  ML: ['fr'],
  BF: ['fr'],
  NE: ['fr'],
  BJ: ['fr'],
  TG: ['fr'],
  GN: ['fr'],
  GW: ['pt'],
  CV: ['pt'],
  SL: ['en'],
  LR: ['en'],
  GM: ['en'],

  // Africa - East/Central
  KE: ['en'],
  TZ: ['en'],
  UG: ['en'],
  ET: ['en'],
  ZW: ['en'],
  ZM: ['en'],
  MW: ['en'],
  SO: ['en'],
  CD: ['fr'],
  CG: ['fr'],
  GA: ['fr'],
  CM: ['fr', 'en'],
  CF: ['fr'],
  TD: ['fr'],
  RW: ['en', 'fr'],
  BI: ['fr', 'en'],
  DJ: ['fr'],
  KM: ['fr'],
  SC: ['en', 'fr'],
  MU: ['en', 'fr'],
  ER: ['en'],
  MR: ['fr'],
  GQ: ['fr', 'pt'],
  ST: ['pt'],
  AO: ['pt'],
  MZ: ['pt'],

  // Africa - Southern
  ZA: ['en'],
  NA: ['en'],
  BW: ['en'],
  LS: ['en'],
  SZ: ['en'],

  // South America
  BR: ['pt'],
  AR: ['es', 'en'],
  CO: ['es'],
  PE: ['es'],
  VE: ['es'],
  CL: ['es', 'en'],
  EC: ['es'],
  BO: ['es'],
  PY: ['es'],
  UY: ['es', 'en'],
  GY: ['en'],
  SR: ['nl', 'en'],
  GF: ['fr'],

  // Central America/Caribbean
  GT: ['es'],
  CU: ['es'],
  DO: ['es'],
  HN: ['es'],
  SV: ['es'],
  NI: ['es'],
  CR: ['es'],
  PA: ['es', 'en'],
  JM: ['en'],
  TT: ['en'],
  BB: ['en'],
  BS: ['en'],
  BZ: ['en', 'es'],
  HT: ['fr'],
  PR: ['es', 'en'],
  LC: ['en'],
  VC: ['en'],
  GD: ['en'],
  AG: ['en'],
  DM: ['en'],
  KN: ['en'],

  // Oceania
  AU: ['en'],
  NZ: ['en'],
  PG: ['en'],
  FJ: ['en'],
  SB: ['en'],
  VU: ['en', 'fr'],
  WS: ['en'],
  TO: ['en'],
  KI: ['en'],
  TV: ['en'],
  NR: ['en'],
  PW: ['en'],
  MH: ['en'],
  FM: ['en'],
  PF: ['fr'],
  NC: ['fr'],
  WF: ['fr'],

  // Other
  GR: ['en'],
  RU: ['en'],
  UA: ['en'],
  BY: ['en'],
  MD: ['ro', 'en'],
  AM: ['en'],
  AZ: ['en'],
  GE: ['en'],
  CY: ['en'],
  MT: ['en'],
  SM: ['it', 'en'],
  VA: ['it'],
  AD: ['es', 'pt', 'fr'],
  MC: ['fr', 'en'],
  AW: ['nl', 'en', 'es'],
  CW: ['nl', 'en', 'es'],
  SX: ['en', 'nl', 'es'],
  MF: ['fr'],
  BL: ['fr'],
  GP: ['fr'],
  MQ: ['fr'],
  RE: ['fr'],
  YT: ['fr'],
  MS: ['en'],
  VG: ['en'],
  VI: ['en'],
  GU: ['en'],
  MP: ['en'],
  AS: ['en'],
  CK: ['en'],
  NU: ['en'],
  TK: ['en'],
};

/**
 * Gets the language priority order for a given country code.
 * Returns an array of supported language codes in order of preference.
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'FR')
 * @returns Array of supported language codes in priority order
 */
export function getLanguageOrderForCountry(countryCode: string): string[] {
  const upperCode = countryCode.toUpperCase();
  const order = COUNTRY_LANGUAGE_ORDER_MAP[upperCode] || ['en']; // Default to English
  
  // Filter to only include supported languages
  const supportedLanguages = ['cs', 'da', 'de', 'en', 'es', 'fr', 'hi', 'hr', 'hu', 'it', 'ja', 'ko', 'nb', 'nl', 'pl', 'pt', 'ro', 'sv', 'tr', 'zh-CN'];
  
  return order.filter(lang => supportedLanguages.includes(lang));
}
